import React, {
  useImperativeHandle,
  useRef,
  useMemo,
  forwardRef,
  useEffect,
} from "react";
import "./resources/scss/main.scss";
import LeftSection from "./components/LeftSection";
import RightSection from "./components/RightSection";
import useUserDetails from "./hooks/useUserDetails";
import useClevertap from "./hooks/useCleverTap";
import { fetchMasterData, MIDDLEWARE } from "./utils/utils";
import SearchBar from "./components/SearchBar";
import CampaignPanel from "@foundit/campaignPanel";
import { useLocalStorageState } from "@foundit/common-functions";
import { useAllServices } from "./hooks/useAllServices";
import { getChannelId, getChannelParam } from "./utils/headerUtils";

interface IVersion {
  json: Record<string, string>,
  db: Record<string, string>
}

interface IProps {
  userData?: any;
  middleware?: string;
  environment: string;
  page_name: string;
  page: string;
  cooldown_time?: number;
  extra_params?: any;
  isClevertapOn: boolean;
  showBetaTag?: boolean;
  containerStyles?: React.CSSProperties;
  containerClasses?: string;
  subContainerStyles?: React.CSSProperties;
  subContainerClasses?: string;
  isRecallGetUser?: boolean;
  isMobileViewEnabled?: boolean;
  isLoggedIn?: boolean;
  hideQuickSearch?: boolean;
  handleEventListener?: ({
    action,
    payload,
    page,
  }: {
    action: string;
    payload?: any;
    page: string;
  }) => void;
  showDrawer?: boolean; // temporary prop to control drawer display
  shouldSendPrevUrl?: boolean; // should send prevUrl on url when redirected to login page
  campaignPanelStyles?: React.CSSProperties;
  showCampaignPanel?: boolean;
  channelId?: number | string;
  subChannelId?: number | string;
  isUseMasterData?: boolean;
  handleMasterDataStatus?: ({status, channelId}:{status: string, channelId: string}) => void; 
}

const RecruiterHeader = forwardRef<any, IProps>((props, ref) => {
  const {
    userData,
    isRecallGetUser,
    middleware,
    environment,
    isClevertapOn,
    page_name,
    page,
    containerStyles,
    containerClasses,
    subContainerStyles,
    subContainerClasses,
    extra_params,
    showBetaTag,
    isMobileViewEnabled,
    isLoggedIn,
    hideQuickSearch,
    handleEventListener,
    showDrawer = true,
    shouldSendPrevUrl = true,
    campaignPanelStyles,
    showCampaignPanel = true,
    channelId = "in",
    subChannelId,
    isUseMasterData = true,
    handleMasterDataStatus
  } = props;

  const isClient = typeof window !== "undefined";
  const isFirstRenderRef = useRef<boolean>(true);

  const { userDetails } = useUserDetails({
    isRecallGetUser,
    isClient,
    middleware: middleware || MIDDLEWARE,
    page_name,
    shouldSendPrevUrl,
  });
  const { version } = useMemo(() => {
    const versionData = userDetails?.master_data_version || {};
    return { version: versionData };
  }, [userDetails]);

  const { data: allServicesData, loading: isServicesLoading } = useAllServices({
    corpId: userDetails?.corp_id,
    recruiterId: userDetails?.recruiter_id,
    middleware,
    version,
    isUseMasterData
  });

  const channelIds = useMemo(() => {
    if (!allServicesData) return [];

    return [
      ...new Set(
        allServicesData
          ?.filter((item: any) => item?.CANUSEFLAG)
          ?.map((item: any) => item?.CHANNELID),
      ),
    ];
  }, [allServicesData]) as (number | string)[];

  const [_, setValue] = useLocalStorageState("isMasterDataLoading", false);

  const isRevamp = useMemo(() => {
    if (typeof window === "undefined") return true;
    const isRevampValue = localStorage?.getItem("isRevamp");
    return isRevampValue !== null ? isRevampValue === "true" : true;
  }, []);

  const effectiveUser = userData || userDetails;

  useClevertap({
    environment,
    extra_params,
    isClevertapOn,
    isClient,
    page_name,
    userData: effectiveUser,
  });

  const navDownloadIconRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getDownloadIconElement: () => navDownloadIconRef.current,
  }));

  const getMasterData = async (
    channelId: number | string,
    version: IVersion,
    middleware?: string,
    subChannelId?: number | string,
  ) => {
    const key = String(channelId);

    let prevVersion: IVersion ={json:{},db:{}};

    try {
      prevVersion = JSON.parse(localStorage.getItem("channelVersion") || "{}");
    } catch {
      prevVersion = {json:{},db:{}};
    }

    if (prevVersion?.json?.[key] !== version?.json?.[key] || prevVersion?.db?.[key] !== version?.db?.[key]) {
      localStorage.removeItem(`master-data-${key}`);
      localStorage.setItem("channelVersion", JSON.stringify(version));
    }

    if (!localStorage.getItem(`master-data-${key}`)) {
      setValue(true);
      if(handleMasterDataStatus){
        handleMasterDataStatus({status: "pending", channelId: key});
      }
      try {
        await fetchMasterData({ channelId, middleware, subChannelId, version});
        if(handleMasterDataStatus){
          handleMasterDataStatus({status: "success", channelId: key})
        }
      } catch (err) {
        console.log(err);
        if(handleMasterDataStatus){
          handleMasterDataStatus({status: "error", channelId: key})
        }
      } finally {
        setValue(false);
      }
    }
  };

  const fetchAllMasterData = async (
    channelIds: (number | string)[],
    version: IVersion,
    middleware?: string,
    subChannelId?: number | string,
  ) => {
    if (!channelIds?.length) return;

    setValue(true);

    try {
      await Promise.all(
        channelIds.map((id) =>
          getMasterData(getChannelParam(id), version, middleware, subChannelId),
        ),
      );
    } catch (err) {
      console.error("Batch master data error", err);
    } finally {
      setValue(false);
    }
  };

  useEffect(() => {
    if (!channelIds.length || !subChannelId || !userDetails) return;

    const remainingChannels = channelIds.filter(
      (id) => id !== getChannelId(`${channelId}`),
    );

    if (!remainingChannels.length) return;

    const run = async () => {
      await fetchAllMasterData(
        remainingChannels,
        version,
        middleware,
        userDetails.subhchannel_id,
      );
    };

    run();
  }, [channelIds, version, middleware, subChannelId, userDetails]);

  useEffect(() => {
    if (!channelId || !subChannelId || !isFirstRenderRef.current || !userDetails || !isUseMasterData) return;

    getMasterData(channelId, version, middleware, subChannelId);
    isFirstRenderRef.current = false;
      
  }, [channelId, middleware, subChannelId, version, userDetails]);

  if (effectiveUser === undefined) return null;

  return (
    <div className="rec_header_root">
      {showCampaignPanel && <CampaignPanel platform="recruiter" />}{" "}
      <div
        className={`rec_header_wrapper ${containerClasses ?? ""}`}
        style={containerStyles}
      >
        <div
          className={`rec_header_container ${
            !userData?.recruiter_id && !userDetails?.recruiter_id
              ? "rec_header_logout_wrapper"
              : ""
          } ${subContainerClasses ?? ""}`}
          style={subContainerStyles}
        >
          <LeftSection
            userData={effectiveUser}
            showBetaTag={showBetaTag}
            isMobileViewEnabled={isMobileViewEnabled}
            isRevamp={isRevamp}
            isLoggedIn={isLoggedIn}
          />

          {!hideQuickSearch && (
            <SearchBar
              middleware={middleware || MIDDLEWARE}
              userDetails={userDetails}
              isClient={isClient}
              handleEventListener={handleEventListener}
              page={page}
            />
          )}
          <RightSection
            middleware={middleware || MIDDLEWARE}
            userData={
              isLoggedIn && !effectiveUser
                ? { recruiter_id: "1", username: "" }
                : effectiveUser
            }
            navDownloadIconRef={navDownloadIconRef}
            showDrawer={showDrawer}
            shouldSendPrevUrl={shouldSendPrevUrl}
          />
        </div>
      </div>
    </div>
  );
});

export default RecruiterHeader;
